import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GlobalHeader from '../components/GlobalHeader';
import Calendar from '../components/calendar/Calendar';
import { API } from '../services/api';
import './SignatureCalendarPage.css';

function SignatureCalendarPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [signatures, setSignatures] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedSignatures, setSelectedSignatures] = useState([]);
    const [showDayDetail, setShowDayDetail] = useState(false);
    const [viewMode, setViewMode] = useState('employee'); // 'employee' ou 'group'
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [adGroups, setAdGroups] = useState([]);

    // URL parameters for filtering
    const urlGroupId = searchParams.get('group') ? parseInt(searchParams.get('group')) : null;
    const urlUserId = searchParams.get('user') ? parseInt(searchParams.get('user')) : null;

    useEffect(() => {
        loadSignatures();
        loadAdGroups();
        loadUsers();
    }, []);

    // Pre-fill selectors from URL parameters
    useEffect(() => {
        if (urlGroupId && adGroups.length > 0) {
            setViewMode('group');
            setSelectedGroup(urlGroupId);
        }
    }, [urlGroupId, adGroups]);

    useEffect(() => {
        if (urlUserId && allUsers.length > 0) {
            setViewMode('employee');
            setSelectedEmployee(urlUserId);
        }
    }, [urlUserId, allUsers]);

    const loadUsers = async () => {
        try {
            const response = await API.getDirectoryUsers();
            if (response && response.data && response.data.users) {
                setAllUsers(response.data.users);
            }
        } catch (err) {
            console.error('Error loading users:', err);
        }
    };

    const loadAdGroups = async () => {
        try {
            const response = await API.getDirectoryGroups();
            if (response && response.data && response.data.groups) {
                setAdGroups(response.data.groups);
            }
        } catch (err) {
            console.error('Error loading AD groups:', err);
        }
    };

    const loadSignatures = async () => {
        setLoading(true);
        try {
            const response = await API.getSignatures();
            if (response && response.data && response.data.signatures) {
                setSignatures(response.data.signatures);
            } else if (response && response.data) {
                setSignatures(Array.isArray(response.data) ? response.data : []);
            }
        } catch (err) {
            console.error('Error loading signatures:', err);
            setError('Erreur lors du chargement des signatures');
        } finally {
            setLoading(false);
        }
    };

    const handleDayClick = (day, daySignatures) => {
        setSelectedDay(day);
        setSelectedSignatures(daySignatures);
        setShowDayDetail(true);
    };

    const handleSignatureClick = (signature) => {
        navigate(`/dashboard?id=${signature.id}`);
    };

    const closeDayDetail = () => {
        setShowDayDetail(false);
        setSelectedDay(null);
        setSelectedSignatures([]);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Non définie';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    // Get selected employee info
    const selectedEmployeeInfo = useMemo(() => {
        if (!selectedEmployee) return null;
        return allUsers.find(u => u.id === selectedEmployee);
    }, [selectedEmployee, allUsers]);

    // Get selected group info
    const selectedGroupInfo = useMemo(() => {
        if (!selectedGroup) return null;
        return adGroups.find(g => g.id === selectedGroup);
    }, [selectedGroup, adGroups]);

    // Filter signatures based on selection
    const filteredSignatures = useMemo(() => {
        if (viewMode === 'employee' && selectedEmployeeInfo) {
            const userGroupIds = selectedEmployeeInfo.group_ids || [];
            const userAssignedSignatureIds = (selectedEmployeeInfo.assigned_signatures || []).map(s => s.id);

            return signatures.filter(sig => {
                // Signature is directly assigned to this user
                if (userAssignedSignatureIds.includes(sig.id)) {
                    return true;
                }
                // Signature is assigned to one of user's groups
                if (sig.ad_group_ids && sig.ad_group_ids.length > 0) {
                    return sig.ad_group_ids.some(groupId => userGroupIds.includes(groupId));
                }
                // Don't show global signatures without specific assignment
                return false;
            });
        }
        if (viewMode === 'group' && selectedGroup) {
            return signatures.filter(sig => {
                if (!sig.ad_group_ids || sig.ad_group_ids.length === 0) {
                    return false; // Don't show global signatures in group view
                }
                return sig.ad_group_ids.includes(selectedGroup);
            });
        }
        return signatures;
    }, [signatures, viewMode, selectedEmployeeInfo, selectedGroup]);

    // Check if a filter is selected
    const hasSelection = (viewMode === 'employee' && selectedEmployee) || (viewMode === 'group' && selectedGroup);

    // Statistiques (use filtered signatures)
    const getStatistics = () => {
        const now = new Date();
        const sigsToUse = filteredSignatures;
        const activeNow = sigsToUse.filter(sig => {
            const validFrom = sig.valid_from ? new Date(sig.valid_from) : null;
            const validUntil = sig.valid_until ? new Date(sig.valid_until) : null;

            if (!validFrom && !validUntil) return sig.is_active;
            if (validFrom && !validUntil) return now >= validFrom && sig.is_active;
            if (!validFrom && validUntil) return now <= validUntil && sig.is_active;

            return now >= validFrom && now <= validUntil && sig.is_active;
        });

        const scheduled = sigsToUse.filter(sig => {
            const validFrom = sig.valid_from ? new Date(sig.valid_from) : null;
            return validFrom && validFrom > now && sig.is_active;
        });

        const expired = sigsToUse.filter(sig => {
            const validUntil = sig.valid_until ? new Date(sig.valid_until) : null;
            return validUntil && validUntil < now;
        });

        return { activeNow, scheduled, expired, total: sigsToUse.length };
    };

    const stats = getStatistics();

    // Handle view mode change - reset selection
    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        if (mode === 'employee') {
            setSelectedGroup(null);
        } else {
            setSelectedEmployee(null);
        }
    };

    return (
        <div className="calendar-page">
            <GlobalHeader />

            <div className="calendar-page-content">
                <div className="calendar-page-header">
                    <div className="header-left">
                        <h1 className="page-title">Calendrier des Signatures</h1>
                        <p className="page-subtitle">
                            {hasSelection ? (
                                viewMode === 'group'
                                    ? `Signatures du groupe "${selectedGroupInfo?.name}"`
                                    : `Signatures de ${selectedEmployeeInfo?.first_name} ${selectedEmployeeInfo?.last_name}`
                            ) : (
                                'Sélectionnez un employé ou un groupe pour afficher le calendrier'
                            )}
                        </p>
                    </div>
                    <button
                        className="btn-back"
                        onClick={() => navigate(-1)}
                    >
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Retour
                    </button>
                </div>

                {/* Contrôles de vue */}
                <div className="calendar-controls">
                    <div className="view-mode-selector">
                        <label className="control-label">Mode d'affichage :</label>
                        <div className="view-mode-buttons">
                            <button
                                className={`view-mode-btn ${viewMode === 'employee' ? 'active' : ''}`}
                                onClick={() => handleViewModeChange('employee')}
                            >
                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                Vue employé
                            </button>
                            <button
                                className={`view-mode-btn ${viewMode === 'group' ? 'active' : ''}`}
                                onClick={() => handleViewModeChange('group')}
                            >
                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                Vue groupe
                            </button>
                        </div>
                    </div>

                    {viewMode === 'employee' && (
                        <div className="filter-selector">
                            <label className="control-label" htmlFor="employee-select">Filtrer par employé :</label>
                            <select
                                id="employee-select"
                                className="filter-select"
                                value={selectedEmployee || ''}
                                onChange={(e) => setSelectedEmployee(e.target.value ? parseInt(e.target.value) : null)}
                            >
                                <option value="">-- Sélectionner un employé --</option>
                                {allUsers.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.first_name} {user.last_name} ({user.email})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {viewMode === 'group' && (
                        <div className="filter-selector">
                            <label className="control-label" htmlFor="group-select">Filtrer par groupe :</label>
                            <select
                                id="group-select"
                                className="filter-select"
                                value={selectedGroup || ''}
                                onChange={(e) => setSelectedGroup(e.target.value ? parseInt(e.target.value) : null)}
                            >
                                <option value="">-- Sélectionner un groupe --</option>
                                {adGroups.map(group => (
                                    <option key={group.id} value={group.id}>
                                        {group.name} ({group.actual_member_count || 0} membres)
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {/* Contenu conditionnel */}
                {!hasSelection ? (
                    <div className="calendar-empty-state">
                        <div className="empty-state-icon">
                            <svg width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3>Sélectionnez un filtre</h3>
                        <p>Choisissez un employé ou un groupe dans le menu ci-dessus pour afficher le calendrier des signatures.</p>
                    </div>
                ) : (
                    <>
                        {/* Statistiques */}
                        <div className="calendar-stats">
                            <div className="stat-card stat-active">
                                <div className="stat-icon">
                                    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-value">{stats.activeNow.length}</div>
                                    <div className="stat-label">Active{stats.activeNow.length > 1 ? 's' : ''} maintenant</div>
                                </div>
                            </div>

                            <div className="stat-card stat-scheduled">
                                <div className="stat-icon">
                                    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-value">{stats.scheduled.length}</div>
                                    <div className="stat-label">Planifiée{stats.scheduled.length > 1 ? 's' : ''}</div>
                                </div>
                            </div>

                            <div className="stat-card stat-expired">
                                <div className="stat-icon">
                                    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-value">{stats.expired.length}</div>
                                    <div className="stat-label">Expirée{stats.expired.length > 1 ? 's' : ''}</div>
                                </div>
                            </div>

                            <div className="stat-card stat-total">
                                <div className="stat-icon">
                                    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-value">{stats.total}</div>
                                    <div className="stat-label">Total</div>
                                </div>
                            </div>
                        </div>

                        {/* Légende */}
                        <div className="calendar-legend">
                            <div className="legend-item">
                                <div className="legend-color legend-active"></div>
                                <span>Signature active</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-color legend-inactive"></div>
                                <span>Signature inactive</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-star">★</span>
                                <span>Signature par défaut</span>
                            </div>
                        </div>

                        {/* Calendrier */}
                        {loading ? (
                            <div className="calendar-loading">
                                <div className="spinner"></div>
                                <p>Chargement du calendrier...</p>
                            </div>
                        ) : error ? (
                            <div className="calendar-error">
                                <p>{error}</p>
                                <button onClick={loadSignatures} className="btn-retry">Réessayer</button>
                            </div>
                        ) : (
                            <Calendar
                                signatures={filteredSignatures}
                                onDayClick={handleDayClick}
                                onSignatureClick={handleSignatureClick}
                                viewMode="single"
                                selectedAdGroup={null}
                                adGroups={adGroups}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Modal de détail du jour */}
            {showDayDetail && (
                <div className="day-detail-overlay" onClick={closeDayDetail}>
                    <div className="day-detail-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="day-detail-header">
                            <h3>Signatures du jour {selectedDay}</h3>
                            <button className="close-btn" onClick={closeDayDetail}>
                                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="day-detail-content">
                            {selectedSignatures.length === 0 ? (
                                <p className="no-signatures">Aucune signature active ce jour</p>
                            ) : (
                                <div className="signatures-list">
                                    {selectedSignatures.map(sig => (
                                        <div
                                            key={sig.id}
                                            className={`signature-detail-item ${sig.is_active ? 'active' : 'inactive'}`}
                                            onClick={() => handleSignatureClick(sig)}
                                        >
                                            <div className="signature-detail-header">
                                                <h4>
                                                    {sig.name}
                                                    {sig.is_default && <span className="default-badge">★ Défaut</span>}
                                                </h4>
                                                <span className={`status-badge ${sig.is_active ? 'status-active' : 'status-inactive'}`}>
                                                    {sig.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            <div className="signature-detail-dates">
                                                <div className="date-info">
                                                    <span className="date-label">Début:</span>
                                                    <span className="date-value">{formatDate(sig.valid_from)}</span>
                                                </div>
                                                <div className="date-info">
                                                    <span className="date-label">Fin:</span>
                                                    <span className="date-value">{formatDate(sig.valid_until)}</span>
                                                </div>
                                            </div>
                                            {sig.priority !== null && (
                                                <div className="signature-priority">
                                                    Priorité: {sig.priority}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SignatureCalendarPage;
